import { useContext, useEffect, useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import {
  faCircleCheck,
  faKey,
  faLinkHorizontalSlash,
  faRedo,
  faSignalStream,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Divider, Paper, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { ConfirmationDialog } from '@scoir/modals'
import { Pill } from '@scoir/pills'

import { CopyToClipboard } from '../ConfigureIntegration/CopyToClipboard'
import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'

const useStyles = makeStyles((theme) => ({
  espContainer: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  description: {
    marginTop: theme.spacing(1),
  },
  switch: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    margin: 0,
  },
  divider: {
    margin: theme.spacing(1, -2),
  },
  keyBox: {
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1, 2),
    border: `1px dotted ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    justifyContent: 'space-between',
  },
  confirmButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.warning.main,
  },
  startIcon: {
    height: 20,
    width: 20,
  },
}))

const CampusESPConfig = () => {
  const classes = useStyles()
  const {
    state,
    onCreateSFTPConfiguration,
    onUpdateSFTPConfiguration,
    onTestSFTPConfiguration,
    onRegenerateSFTPKey,
    campusESPTestSuccessful,
  } = useContext(IntegrationConfigurationContext)

  const { enabled, username, publicKey } = state.campusESPConfiguration || {}

  const [editing, setEditing] = useState(false)
  const [newUsername, setNewUsername] = useState(username || '')
  const [testingConnection, setTestingConnection] = useState(false)
  const [updatingKey, setUpdatingKey] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState<'testing' | 'disconnect' | undefined>(undefined)

  const handleTestingConfiguration = () => {
    setTestingConnection(true)
    onTestSFTPConfiguration({ id: state.campusESPConfiguration!.id, username: newUsername })
  }

  const handleConnection = (showConfirmation: boolean) => {
    if (showConfirmation) {
      // Display confirmation before disconnecting the CampusESP integration
      setShowConfirmation('disconnect')
    } else if (enabled) {
      // User is attempting to update the CampusESP configuration - test the connection again
      setShowConfirmation(undefined)
      handleTestingConfiguration()
    } else {
      setShowConfirmation(undefined)
      onUpdateSFTPConfiguration({
        id: state.campusESPConfiguration!.id,
        enabled: !enabled,
        username: newUsername,
      })
    }
  }

  const handleOnClose = () => {
    setShowConfirmation(undefined)
    setTestingConnection(false)
    setNewUsername(username || '')
  }

  useEffect(() => {
    if (campusESPTestSuccessful !== undefined) {
      setTestingConnection(false)
    }
  }, [campusESPTestSuccessful])

  return (
    <Paper
      elevation={0}
      className={classes.espContainer}
    >
      <Box
        display="flex"
        justifyContent="space-between"
      >
        <div>
          <Box
            display="flex"
            alignItems="center"
            gridGap={8}
          >
            <Typography>Connect to CampusESP</Typography>
            {enabled ? (
              <Pill color={state.SendParentHandraisersBeforeStudent ? 'success' : 'info'}>
                <Box
                  display="flex"
                  gridGap={4}
                  alignItems="center"
                >
                  <FontAwesomeIcon icon={faCircleCheck} />
                  {state.SendParentHandraisersBeforeStudent ? 'Activated' : 'Ready to Activate'}
                </Box>
              </Pill>
            ) : (
              <Pill>Not Connected</Pill>
            )}
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.description}
          >
            Deliver the parent handraisers file directly to the school's CampusESP account.
          </Typography>
        </div>
        <Button
          onClick={() => setEditing(!editing)}
          color="primary"
          endIcon={<FontAwesomeIcon icon={editing ? faChevronUp : faChevronDown} />}
        >
          {enabled ? 'Edit' : 'Setup'}
        </Button>
      </Box>

      {editing && (
        <>
          <Divider className={classes.divider} />
          <Box m={1}>
            <Typography variant="h6">Credentials</Typography>
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Enter the CampusESP username, then generate a publicKey and share it with CampusESP to authorize file
              delivery.{' '}
              <a
                href="" // TODO: Add the actual link to the CampusESP documentation here
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more.
              </a>
            </Typography>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              color="primary"
              name="username"
              label="Username"
              id="username"
              autoComplete="username"
              required
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              disabled={testingConnection}
            />
            {publicKey ? (
              <Box my={2}>
                <CopyToClipboard
                  label="Key"
                  value={publicKey}
                  disabled={testingConnection}
                />
                {updatingKey ? (
                  <Box my={2}>
                    <Alert
                      severity="warning"
                      action={
                        <Box
                          display="flex"
                          gridGap={8}
                        >
                          <Button
                            color="inherit"
                            onClick={() => setUpdatingKey(false)}
                            size="small"
                          >
                            CANCEL
                          </Button>
                          <Button
                            variant="contained"
                            className={classes.confirmButton}
                            onClick={() => {
                              onRegenerateSFTPKey({ id: state.campusESPConfiguration!.id })
                              setUpdatingKey(false)
                            }}
                            size="small"
                          >
                            REGENERATE
                          </Button>
                        </Box>
                      }
                    >
                      <Typography variant="body2">
                        Regenerating replaces the current publicKey. CampusESP deliveries will stop until the new
                        publicKey is added in CampusESP.
                      </Typography>
                    </Alert>
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={2}
                  >
                    <Button
                      color="primary"
                      startIcon={<FontAwesomeIcon icon={faRedo} />}
                      onClick={() => setUpdatingKey(true)}
                      disabled={testingConnection}
                    >
                      Regenerate
                    </Button>
                    {/* Display Activate/Disconnect after a successful test and with no config changes */}
                    {(campusESPTestSuccessful || enabled) && username === newUsername ? (
                      <Button
                        variant={enabled ? 'outlined' : 'contained'}
                        color="primary"
                        onClick={() => handleConnection(enabled ?? false)}
                        startIcon={
                          <FontAwesomeIcon
                            icon={enabled ? faLinkHorizontalSlash : faCircleCheck}
                            className={classes.startIcon}
                          />
                        }
                      >
                        {enabled ? 'Disconnect' : 'Activate Connection'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          if (enabled) {
                            // Display disconnect confirmation before retesting the CampusESP integration
                            setShowConfirmation('testing')
                          } else {
                            handleTestingConfiguration()
                          }
                        }}
                        startIcon={<FontAwesomeIcon icon={testingConnection ? faSpinner : faSignalStream} />}
                        disabled={!newUsername || !publicKey || testingConnection}
                      >
                        {testingConnection ? 'Testing...' : 'Test Connection'}
                      </Button>
                    )}
                  </Box>
                )}
                {campusESPTestSuccessful !== undefined && !testingConnection && !updatingKey && (
                  <Box mt={2}>
                    <Alert severity={campusESPTestSuccessful ? 'success' : 'error'}>
                      {campusESPTestSuccessful
                        ? 'Test Connection successful.'
                        : 'Test Connection failed. Please try again or contact support.'}
                    </Alert>
                  </Box>
                )}
              </Box>
            ) : (
              <Box className={classes.keyBox}>
                <div>
                  <Typography>Key</Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    No publicKey yet. Generate one to share with CampusESP.
                  </Typography>
                </div>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FontAwesomeIcon icon={faKey} />}
                  disabled={!newUsername}
                  onClick={() => onCreateSFTPConfiguration({ type: 'campusESP', username: newUsername })}
                >
                  GENERATE KEY
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}
      <ConfirmationDialog
        open={Boolean(showConfirmation)}
        headerText="Disconnect CampusESP Integration"
        onClose={handleOnClose}
        onConfirm={() => handleConnection(false)}
        confirmationText="Disconnect"
      >
        <Typography>
          Are you sure you want to disconnect the the current CampusESP integration? CampusESP deliveries will stop
          until the integration is reconnected.
        </Typography>
      </ConfirmationDialog>
    </Paper>
  )
}

export default CampusESPConfig
